import { NRC_DATA, NRC_REGEX, NRC_TYPES } from "@/constants";
import { convertBurmeseToEnglishNumerals, convertEnglishToBurmeseNumerals, getNRCTownshipCode } from "@/libs/utils";
import { useEffect, useState } from "react";

export type NRCPartsType = {
    nrc_code: string;
    township_code: string;
    nrc_type: string;
    nrc_number: string;
}

const getTownships = (nrcCode: string) => {
    return NRC_DATA.filter((nrc) => nrc.nrc_code === nrcCode).map((nrc) => nrc.name_mm);
}

const formatNrcString = (parts: NRCPartsType) => {
    const { nrc_code, township_code, nrc_type, nrc_number } = parts;
    return `${nrc_code}/${township_code}(${nrc_type})${nrc_number}`
};

export const useNRC = (nrcString: string): {
    nrcParts: NRCPartsType,
    townships: string[],
    fullNRCString: string,
    onNRCNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onSelectChange: (value: string, type: 'nrc_code' | 'township_code' | 'nrc_type') => void
} => {
    const defaultTownship = getTownships(Number(1).toString())[0] || '';

    const [townships, setTownships] = useState<string[]>(getTownships(Number(1).toString()));
    const [fullNRCString, setFullNRCString] = useState<string>(nrcString);

    const [nrcParts, setNRCParts] = useState<NRCPartsType>({
        nrc_code: Number(1).toString(), // Default to 1
        township_code: getNRCTownshipCode(defaultTownship), // Default to first township code
        nrc_type: NRC_TYPES[0], // Default to first type
        nrc_number: ''
    })

    useEffect(() => {
        if (nrcString !== "") {
            const match = nrcString.match(NRC_REGEX);
            if (match) {
                setNRCParts({
                    nrc_code: /^[1-9]|1[0-4]{1,2}/.test(match[1]) ? match[1] : convertBurmeseToEnglishNumerals(match[1]),
                    township_code: match[2],
                    nrc_type: match[3],
                    nrc_number: match[4]
                });
                setTownships(getTownships(convertBurmeseToEnglishNumerals(match[1])));
            }
        } else {
            // Reset to default values if nrcString is empty
            setNRCParts({
                nrc_code: Number(1).toString(),
                township_code: getNRCTownshipCode(defaultTownship),
                nrc_type: NRC_TYPES[0],
                nrc_number: ''
            });
            setTownships(getTownships(Number(1).toString()));
        }
    }, [nrcString, defaultTownship]);

    useEffect(() => {
        const formattedNrcString = formatNrcString({
            ...nrcParts,
            nrc_code: /^[1-9]|1[0-4]{1,2}/.test(nrcParts.nrc_code) ? convertEnglishToBurmeseNumerals(nrcParts.nrc_code) : nrcParts.nrc_code,
        });
        setFullNRCString(formattedNrcString);
    }, [nrcParts]);

    const onNRCCodeChange = (value: string) => {
        const filteredTownships = getTownships(value);
        const defaultTownshipCode = filteredTownships.length > 0 ? getNRCTownshipCode(filteredTownships[0]) : '';

        setTownships(filteredTownships);
        setNRCParts(prev => ({
            ...prev,
            nrc_code: convertEnglishToBurmeseNumerals(value),
            township_code: defaultTownshipCode // Automatically update township code
        }));
    }

    const onTownshipChange = (value: string) => {
        setNRCParts(prev => ({ ...prev, township_code: value }));
    };

    const onNRCTypeChange = (value: string) => {
        setNRCParts(prev => ({ ...prev, nrc_type: value }));
    };

    const onSelectChange = (value: string, type: 'nrc_code' | 'township_code' | 'nrc_type') => {
        switch (type) {
            case 'nrc_code':
                onNRCCodeChange(value);
                break;
            case 'township_code':
                onTownshipChange(value);
                break;
            case 'nrc_type':
                onNRCTypeChange(value);
                break;
            default:
                break;
        }
    }

    const onNRCNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNRCParts(prev => ({ ...prev, nrc_number: convertEnglishToBurmeseNumerals(value) }));
    };

    return {
        nrcParts,
        townships,
        fullNRCString,
        onNRCNumberChange,
        onSelectChange
    };
}